import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { ArrowLeft, MessageSquare, Moon, Repeat, Sun, Trophy } from 'lucide-react';
import { useTheme } from '@/components/theme-provider';
import { useUiModeStore } from '@/stores/ui-mode-store';
import { useGuidedUiMode } from '@/hooks/use-guided-ui-mode';
import { useMessageStore } from '@/stores/message-store';
import { useTranslations } from '@/i18n/useTranslations';
import { cn } from '@/lib/utils';
import { AccountMenu } from './account-menu';
import { LanguageSwitcher } from '@/components/ui/language-switcher';

const ICON_BTN =
  'relative inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground';

/**
 * 居中毛玻璃单卡容器(引导式各页共用,持久于 GuidedShell)。
 *
 * 卡片本身不滚动——随内容 morph(ResizeObserver + 每渲染测 → CSS transition height/max-width)。
 * "只有数据单元滚动"由各页自己实现:页面里的数据列表(如首页赛项列表)自带 max-h + overflow-y-auto,
 * 卡内其余(标题/CTA/chrome)静止不动。卡片高度因此天然 < 屏幕(因数据列表已被各页封顶)。
 *
 * size:'md' 焦点页 / 'wide' 工具表单页。安全面框 hsl(var(--surface-border)) 避开 glass/cyber 黑框。
 * 尊重减少动效。AccountMenu 下拉走 Portal,overflow:hidden 不裁剪它。
 */
export function CenteredGlassCard({
  children,
  size = 'md',
}: {
  children: ReactNode;
  size?: 'md' | 'wide';
}) {
  const t = useTranslations('guided');
  const setUiMode = useUiModeStore((s) => s.setUiMode);
  const guidedUiMode = useGuidedUiMode();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const isHome = location.pathname === '/dashboard' || location.pathname === '/dashboard/';
  const prefersReducedMotion = useReducedMotion();

  // 卡随内容高度 morph(不滚动整页):RO(异步)+ 每次渲染后测(React 驱动),仅变化时触发
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const measure = () => {
    const el = contentRef.current;
    if (!el) return;
    const h = el.scrollHeight;
    if (h > 0) setContentHeight((prev) => (prev === h ? prev : h));
  };
  useLayoutEffect(measure);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="flex h-dvh items-center justify-center overflow-hidden p-4 py-10">
      <div
        style={{
          height: prefersReducedMotion || contentHeight == null ? undefined : contentHeight,
          maxHeight: 'calc(100dvh - 5rem)',
          overflow: 'hidden',
          transition: prefersReducedMotion
            ? undefined
            : 'height 0.3s cubic-bezier(0.32, 0.72, 0, 1), max-width 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
        }}
        className={cn(
          'w-full rounded-3xl border border-[color:hsl(var(--surface-border))] bg-overlay/60 shadow-elev backdrop-blur-2xl',
          size === 'wide' ? 'max-w-5xl' : 'max-w-md',
        )}
      >
        <div ref={contentRef} className="space-y-6 p-6 sm:p-8">
          {/* 卡内顶栏 chrome(brand/返回 + 消息/主题/经典/账号) */}
          <div className="-mx-1 flex items-center justify-between gap-2">
            <div>
              {isHome ? (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-1.5 rounded-lg px-1 py-1 text-sm font-semibold"
                >
                  <Trophy className="h-4 w-4 text-primary" />
                  <span className="truncate">{t('appName')}</span>
                </Link>
              ) : (
                <Link
                  to="/dashboard"
                  aria-label={t('back')}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('back')}
                </Link>
              )}
            </div>
            <div className="flex items-center gap-0.5">
              <Link to="/dashboard/messages" aria-label={t('messages')} className={ICON_BTN}>
                <MessageSquare className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-primary-foreground">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Link>
              <LanguageSwitcher compact />
              <button
                type="button"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                aria-label={t('theme')}
                className={ICON_BTN}
              >
                <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              </button>
              {guidedUiMode !== 'force' && (
                <button
                  type="button"
                  onClick={() => setUiMode('classic')}
                  aria-label={t('switchClassic')}
                  className={ICON_BTN}
                >
                  <Repeat className="h-4 w-4" />
                </button>
              )}
              <AccountMenu />
            </div>
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
